import { IsofixGlyph } from '../ui/Artwork'
import {
  CAP_FACE,
  CAP_FLANGE,
  CAP_GLINT,
  CAP_GLINT_DIM,
  CAP_LIT,
  CAP_OUTLINE,
  CAP_ROLL_FRONT,
  CAP_ROLL_SIDE,
  CAP_SHADOW,
  CAP_TURN,
  CAP_WALL_FRONT,
  CAP_WALL_SIDE,
} from './capShape'
import { Box } from './kit'
import { box, cylinder, P, poly } from './projection'

/* ==========================================================================
   Pad-printing tooling: the steel cliché on its machined riser, the sealed
   ink cup and its drive on an L-bracket, and the fixture holding the real
   part — the black ISOFIX cap (portfolio-new-4: a rounded moulded tile with
   a rolled perimeter and a white mark; geometry in capShape.js).
   Stage coordinates: the cliché image centre and the cap's crown both sit at
   y 550, at x 484 and 1124 (the two pad contacts).
   ========================================================================== */

const CLICHE_FACE = box({ x0: 250, x1: 656, z0: 46, z1: 146, h0: 96, h1: 102 })
const RISER = box({ x0: 240, x1: 690, z0: 20, z1: 150, h0: 0, h1: 96 })
const CLAMPS = [250, 644].map((x0) => box({ x0, x1: x0 + 12, z0: 40, z1: 152, h0: 102, h1: 108 }))
const CLAMP_SCREWS = [256, 650]
  .flatMap((x) => [64, 128].map((z) => [x + 0.18 * z, 700 - 108 - 0.5 * z]))
  .map(([x, y]) => `M${(x + 2.5).toFixed(1)} ${y.toFixed(1)}a2.5 1.4 0 1 0 -5 0a2.5 1.4 0 1 0 5 0`)
  .join('')
const SHINE = 'M585 575H603L631.9 525H613.9ZM612 575H619L647.9 525H640.9Z'

const FACE_UNIT = 'matrix(1 0 -0.18 0.5 0 0)'
const CLICHE_MARK = 'matrix(0.8 0 -0.144 0.4 484 550)'
const PART_MARK = 'matrix(0.8 0 -0.144 0.4 1124 550)'

/* Riser front (z 20 plane, stage x 243.6..693.6, y 594..690): a chamfer
   under the top edge and two machined pockets with socket screws, so the
   face holds up when the camera pushes in. */
const POCKETS = [
  [262, 456],
  [480, 674],
]
const POCKET = POCKETS.map(([a, b]) => `M${a} 612H${b}V668H${a}Z`).join('')
const POCKET_FLOOR = POCKETS.map(([a, b]) => `M${a} 664.5H${b}V668H${a}Z`).join('')
const POCKET_WALL = POCKETS.map(([a]) => `M${a} 612h1.6v56h-1.6Z`).join('')
const POCKET_LIP = POCKETS.map(([a, b]) => `M${a} 668H${b}`).join('')
const socket = (x, y) => `M${x + 3.4} ${y}a3.4 3.4 0 1 0 -6.8 0a3.4 3.4 0 1 0 6.8 0`
const RISER_SCREWS = POCKETS.flatMap(([a, b]) => [a + 16, b - 16].flatMap((x) => [626, 654].map((y) => socket(x, y)))).join('')
const RISER_SOCKETS = POCKETS.flatMap(([a, b]) => [a + 16, b - 16].flatMap((x) => [626, 654].map((y) => `M${x - 1.2} ${y - 1.2}h2.4v2.4h-2.4Z`))).join('')

function RiserDetail() {
  return (
    <g>
      <rect className="f-gF" x="243.6" y="594" width="450" height="3.5" />
      <path className="f-gR" d={POCKET} />
      <path className="f-gD" d={POCKET_WALL} />
      <path className="f-gF" d={POCKET_FLOOR} />
      <path className="l-gT" d={POCKET_LIP} />
      <path className="f-gF pp-hair" d={RISER_SCREWS} />
      <path className="f-gD pp-hair" d={RISER_SOCKETS} />
    </g>
  )
}

/** Steel cliché on an aluminium riser; the static part of the module. */
export function ClicheBase({ id }) {
  return (
    <g>
      <path className="f-gR" d={RISER.shadow} />
      <Box geo={RISER} mat="d" />
      <RiserDetail />
      <path className="f-sE" d={CLICHE_FACE.side} />
      <path className="f-sE" d={CLICHE_FACE.front} />
      <path className="f-sF" d={CLICHE_FACE.top} />
      <path className="f-sSh" d={SHINE} clipPath={`url(#${id}clicheFace)`} />
      <path className="l-spec" d={CLICHE_FACE.litTop} />
    </g>
  )
}

/** The clamp bars over the plate ends and the mark etched in four layers. */
export function ClicheTop() {
  return (
    <g>
      {CLAMPS.map((geo) => (
        <Box key={geo.top} geo={geo} />
      ))}
      <path className="f-aS pp-hair" d={CLAMP_SCREWS} />
      <g transform={CLICHE_MARK}>
        <g transform="translate(1.16 2.25)">
          <IsofixGlyph className="pp-glyph g-lip" />
        </g>
        <IsofixGlyph className="pp-glyph g-groove" />
        <IsofixGlyph className="pp-glyph g-ink pp-etch-ink" />
        <IsofixGlyph className="pp-glyph g-res pp-etch-res" />
      </g>
    </g>
  )
}

/* ---- Cup drive: a small cylinder on an L-bracket off the riser's left face */

const BRACKET_PLATE = box({ x0: 226, x1: 240, z0: 70, z1: 118, h0: 40, h1: 124 })
const BRACKET_SHELF = box({ x0: 146, x1: 240, z0: 80, z1: 112, h0: 110, h1: 118 })
const GUSSET = poly([P(226, 110, 88), P(166, 110, 88), P(226, 54, 88)])
const GUSSET_EDGE = `M${P(166, 110, 88).join(' ')}L${P(226, 54, 88).join(' ')}`
const DRIVE = box({ x0: 150, x1: 236, z0: 84, z1: 108, h0: 118, h1: 142 })

export function CupDrive() {
  return (
    <g>
      <Box geo={BRACKET_PLATE} mat="g" />
      <path className="f-gS" d={GUSSET} />
      <path className="l-gT" d={GUSSET_EDGE} />
      <Box geo={BRACKET_SHELF} mat="g" />
      <path className="f-gR pp-hair" d={`${socket(245.6, 556)}${socket(245.6, 576)}`} />
      <Box geo={DRIVE} />
      <path className="f-aS" d="M165 514h8v28h-8ZM243 514h8v28h-8Z" />
      <path className="l-aS pp-hair" d="M180 528H236" />
    </g>
  )
}

/** The cup rod (moves with the cup). */
export function CupRod() {
  return (
    <g className="pp-cup-rod">
      <rect className="f-cA" x="0" y="518" width="1" height="2" />
      <rect className="f-cB" x="0" y="520" width="1" height="3.6" />
      <rect className="f-cC" x="0" y="523.6" width="1" height="2.4" />
    </g>
  )
}

/* ---- Ink cup: sealed aluminium cup on a ceramic doctor ring ------------ */

const CUP = cylinder(55, 60, [[0, 0.22], [0.7, 1]])
const RING = cylinder(55, 8)
const KNOB = cylinder(13, 12, [[0, 0.3], [0.68, 1]])
const top = (r, y) => <circle r={r} transform={`matrix(1 0 -0.18 0.5 0 ${y})`} />

export function InkCup() {
  return (
    <g className="pp-cup">
      <circle className="f-sE" r="55" transform="matrix(1 0 -0.18 0.5 12 -4)" />
      <path className="f-aF" d={CUP.body} />
      <path className="f-aT" d={CUP.bands[0]} />
      <path className="f-aS" d={CUP.bands[1]} />
      <path className="f-cer" d={RING.body} />
      <path className="l-spec" d={RING.foot} />
      <path className="l-aS" d={RING.rim} />
      <g className="f-aF">{top(55, -60)}</g>
      <g className="f-aT">{top(50, -60)}</g>
      <path className="l-spec" d={`M${(-CUP.xm).toFixed(1)} ${(-CUP.ym - 60).toFixed(1)}V${(-CUP.ym).toFixed(1)}`} />
      <path className="l-spec" d="M-9.6 54.2A55 55 0 0 1 -51.7 18.8" transform="matrix(1 0 -0.18 0.5 0 -60)" />
      <rect className="f-aS" x="-62" y="-34" width="8" height="12" />
      <g transform="translate(0 -60)">
        <path className="f-aF" d={KNOB.body} />
        <path className="f-aT" d={KNOB.bands[0]} />
        <path className="f-aS" d={KNOB.bands[1]} />
        <g className="f-aT">{top(13, -12)}</g>
        <path className="l-aS pp-hair" d="M-4 -9V2.6M0 -9V2.8M4 -9V2.6" />
      </g>
    </g>
  )
}

/* ---- Fixture and the ISOFIX cap ------------------------------------------ */

const BASE = box({ x0: 960, x1: 1290, z0: 0, z1: 160, h0: 0, h1: 18 })
const NEST = box({ x0: 1010, x1: 1210, z0: 10, z1: 150, h0: 18, h1: 58 })
const PIN = cylinder(4, 9, [[0, 0.3], [0.65, 1]])

export function Fixture() {
  return (
    <g>
      <path className="f-gR" d={BASE.shadow} />
      <Box geo={BASE} mat="d" />
      <path className="f-aF" d={NEST.shadow} />
      <Box geo={NEST} />
      <path className="l-aS pp-hair" d="M1012 657H1212" />
      {[
        [1038.4, 602],
        [1210.4, 602],
      ].map(([x, y]) => (
        <g key={x} transform={`translate(${x} ${y})`}>
          <path className="f-cB" d={PIN.body} />
          <path className="f-cA" d={PIN.bands[0]} />
          <path className="f-cC" d={PIN.bands[1]} />
          <g className="f-cA">{top(4, -9)}</g>
        </g>
      ))}
    </g>
  )
}

/** The moulded body of the cap (static): walls, rolled perimeter, crowned face. */
export function CapBody({ id, shadow = true }) {
  return (
    <g>
      {shadow && <path className="f-aF" d={CAP_SHADOW} />}
      <path className="f-pS" d={CAP_WALL_SIDE} />
      <path className="f-pW" d={CAP_WALL_FRONT} />
      <path className="f-pR2" d={CAP_ROLL_SIDE} />
      <path className="f-pR" d={CAP_ROLL_FRONT} />
      <path className="f-pR2" d={CAP_FLANGE} />
      <path className="f-pR" d={CAP_OUTLINE} />
      <path className="f-pF" d={CAP_FACE} />
      <path className="f-pL" d={CAP_LIT} />
      <path className="f-pD" d={CAP_TURN} />
      <path d={CAP_FACE} fill={`url(#${id}stipple)`} />
    </g>
  )
}

/** The glint round the rolled perimeter and the mark (prints at `part-ink`). */
export function CapMark() {
  return (
    <g>
      <path className="l-crest" d={CAP_GLINT} />
      <path className="l-crestDim" d={CAP_GLINT_DIM} />
      <g transform={PART_MARK}>
        <IsofixGlyph className="pp-glyph g-ink pp-part-ink" />
      </g>
    </g>
  )
}

/** The whole cap as one piece (the assembly film carries it). */
export function Cap({ id, shadow = true }) {
  return (
    <g>
      <CapBody id={id} shadow={shadow} />
      <CapMark />
    </g>
  )
}

/** Defs the pad tooling needs: clip of the cliché face, the cap face, the stipple. */
export function PadPartDefs({ id, left = true, right = true }) {
  return (
    <>
      {left && (
        <clipPath id={`${id}clicheFace`} clipPathUnits="userSpaceOnUse">
          <path d={CLICHE_FACE.top} />
        </clipPath>
      )}
      {right && (
        <clipPath id={`${id}capFace`} clipPathUnits="userSpaceOnUse">
          <path d={CAP_OUTLINE} />
        </clipPath>
      )}
      {right && <Stipple id={id} />}
    </>
  )
}

/** The moulded grain of the polymer parts: a stipple lying in the face plane. */
export function Stipple({ id }) {
  return (
    <pattern id={`${id}stipple`} patternUnits="userSpaceOnUse" width="24" height="24" patternTransform={FACE_UNIT}>
      <path className="f-pDot" d="M3.8 4a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M12.8 2a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M20.8 6a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M7.8 11a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M16.8 13a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M23.8 12a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M2.8 19a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M11.8 21a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0M19.8 20a.8.8 0 1 0-1.6 0a.8.8 0 1 0 1.6 0" />
    </pattern>
  )
}
